import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { List, X, AppleLogo, GooglePlayLogo } from '@phosphor-icons/react';
import { NAV_LINKS, COMPANY_INFO, APP_STORE_URL, GOOGLE_PLAY_URL } from '@/utils/constants';
import { theme } from '@/styles/theme';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import { useIsMobile } from '@/hooks/useMediaQuery';
//@ts-ignore
import mascotHead from '@/assets/mascot_head.png';

const HeaderWrapper = styled(motion.header)`
  position: fixed;
  top: ${theme.spacing.md};
  left: 10%;
  right: 10%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: ${theme.shadows.medium};
  z-index: ${theme.zIndex.sticky};
  border-radius: ${theme.borderRadius.full};

  @media (max-width: ${theme.breakpoints.tablet}) {
    top: ${theme.spacing.sm};
    left: ${theme.spacing.sm};
    right: ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.large};
  }
`;

const HeaderInner = styled.div`
  max-width: ${theme.breakpoints.wide};
  margin: 0 auto;
  padding: ${theme.spacing.sm} ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.md} ${theme.spacing.md};
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.lg};
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-family: ${theme.fonts.secondary};
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.black};
  color: ${theme.colors.primary.main};
  text-decoration: none;
  transition: color ${theme.transitions.fast};
  flex-shrink: 0;
  letter-spacing: -0.5px;
  -webkit-text-stroke: 0.5px ${theme.colors.primary.main};

  &:hover {
    color: ${theme.colors.primary.dark};
    -webkit-text-stroke-color: ${theme.colors.primary.dark};
  }
`;

const MascotLogo = styled.img`
  height: 36px;
  width: auto;

  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 32px;
  }
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 80%;
    max-width: 300px;
    background: ${theme.colors.white};
    flex-direction: column;
    align-items: flex-start;
    padding: ${theme.spacing.xxxl} ${theme.spacing.xl};
    box-shadow: ${theme.shadows.large};
    transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(100%)')};
    transition: transform ${theme.transitions.normal};
    z-index: ${theme.zIndex.modal + 1};
    border-radius: 0;
  }
`;

const NavLink = styled.a<{ $active: boolean }>`
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.semibold};
  color: ${({ $active }) => ($active ? theme.colors.primary.main : theme.colors.text.darkGray)};
  text-decoration: none;
  transition: color ${theme.transitions.fast};
  position: relative;
  cursor: pointer;

  &:hover {
    color: ${theme.colors.primary.main};
    font-weight: ${theme.fontWeights.bold};
  }



  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.fontSizes.lg};
    width: 100%;

    &::after {
      display: none;
    }
  }
`;

const DownloadButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const DownloadButton = styled.a<{ $variant: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  border-radius: ${theme.borderRadius.full};
  text-decoration: none;
  transition: all ${theme.transitions.fast};

  ${({ $variant }) =>
    $variant === 'primary'
      ? `
    background: ${theme.colors.primary.main};
    color: ${theme.colors.white};

    &:hover {
      color: ${theme.colors.white};
      transform: scale(1.05);
    }
  `
      : `
    background: ${theme.colors.neutral[800]};
    color: ${theme.colors.white};

    &:hover {
      color: ${theme.colors.white};
      transform: scale(1.05);
    }
  `}

  &:active {
    transform: scale(0.98);
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: ${theme.spacing.sm};
  color: ${theme.colors.text.black};
  cursor: pointer;
  z-index: ${theme.zIndex.modal + 1};

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
    transition: opacity ${theme.transitions.normal};
    z-index: ${theme.zIndex.overlay};
  }
`;

// Spacer to prevent content from going under the fixed header
const HeaderSpacer = styled.div`
  height: 80px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 70px;
  }
`;

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const { scrollDirection, isAtTop } = useScrollDirection();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // Determine if header should be hidden
  const shouldHide = !isAtTop && scrollDirection === 'down' && !isMenuOpen;

  // Handle smooth scroll navigation
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    // Check if it's a hash link on the current page
    if (path.startsWith('/#')) {
      e.preventDefault();
      const targetId = path.replace('/#', '');
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        closeMenu();
        targetElement.scrollIntoView({ behavior: 'smooth' });
      } else if (location.pathname !== '/') {
        // If we're not on the home page, navigate there first
        window.location.href = path;
      }
    }
  };

  return (
    <>
      <HeaderWrapper
        initial={{ y: 0 }}
        animate={{ y: shouldHide ? -100 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <HeaderInner>
          <HeaderContent>
            <Logo to="/" onClick={closeMenu}>
              <MascotLogo src={mascotHead} alt="Lexifox mascot" />
              {COMPANY_INFO.name}
            </Logo>

            <Nav $isOpen={isMenuOpen}>
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  href={link.path}
                  $active={location.pathname === '/' && link.path.startsWith('/#')}
                  onClick={(e) => handleNavClick(e, link.path)}
                >
                  {link.label}
                </NavLink>
              ))}
            </Nav>

            <DownloadButtons>
              <DownloadButton href={APP_STORE_URL} $variant="primary">
                <AppleLogo size={18} weight="fill" />
                iOS
              </DownloadButton>
              <DownloadButton href={GOOGLE_PLAY_URL} $variant="secondary">
                <GooglePlayLogo size={18} weight="fill" />
                Android
              </DownloadButton>
            </DownloadButtons>

            {isMobile && (
              <MenuButton onClick={toggleMenu} aria-label="Toggle menu">
                {isMenuOpen ? <X size={28} /> : <List size={28} />}
              </MenuButton>
            )}
          </HeaderContent>
        </HeaderInner>
        <Overlay $isOpen={isMenuOpen} onClick={closeMenu} />
      </HeaderWrapper>

      <HeaderSpacer />
    </>
  );
};
